import { Link, useNavigate, type LinkComponent } from "@tanstack/react-router";
import type { UseNavigateResult } from "@tanstack/react-router";
import { inventorySearchSchema } from "./inventory-scope-codec";

const INVENTORY_SCOPE_KEYS = Object.keys(inventorySearchSchema.shape) as Array<
  keyof typeof inventorySearchSchema.shape
>;

// Forwarding the *entire* previous search object (including route-local leaf params such as
// /findings' `category`/`source` — findings.tsx:15-22) would make those leaf params sticky
// across every navigation, since `prev` is the full merged search state regardless of the
// target route. Only the root-owned inventory scope (building/group/rajaus — issue #536) is
// meant to survive an unrelated navigation, so pick just those keys back out of `prev`.
// eslint-disable-next-line react-refresh/only-export-components -- exported for scoped-nav.test.ts
export function pickInventoryScope(
  prev: Record<string, unknown>,
): Record<string, unknown> {
  const scope: Record<string, unknown> = {};
  for (const key of INVENTORY_SCOPE_KEYS) {
    if (prev[key] !== undefined) scope[key] = prev[key];
  }
  return scope;
}

// Internal navigation must preserve inventory scope (building/group/rajaus — see issue #536)
// across route changes. TanStack Router does not do this by default: a plain `Link`/`navigate()`
// drops search params unless told to keep them. Route every in-app navigation through these
// wrappers instead of `Link`/`useNavigate` directly, so preserving scope is the default rather
// than a convention every call site has to remember. Callers that need a route-local leaf param
// to survive (e.g. LensTopicSection's "Havainnot →" deep-link) pass their own explicit `search`
// prop/option, which overrides this default entirely.
export const ScopedLink: LinkComponent<"a"> = (props) => {
  return <Link search={(prev) => pickInventoryScope(prev)} {...props} />;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useScopedNavigate(): UseNavigateResult<string> {
  const navigate = useNavigate();
  const scopedNavigate: UseNavigateResult<string> = (opts) =>
    navigate({ search: (prev) => pickInventoryScope(prev), ...opts });
  return scopedNavigate;
}
